'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/lib/auth/context'
import { isAdminUserClient } from '@/lib/admin/allowlist'
import { captureVisitOrigin, originProperties } from '@/lib/telemetry/attribution'
import { EVENT_CATEGORIES, PRODUCT_EVENTS, normalizeTelemetryPath } from '@/lib/telemetry/constants'
import { sendTelemetryEvents } from '@/lib/telemetry/send-client'
import { getSessionId } from '@/lib/telemetry/session'

const MIN_ENGAGEMENT_SEC = 2

function shouldTrack(path: string): boolean {
  if (path.startsWith('/_next') || path.startsWith('/api')) return false
  if (path.startsWith('/admin')) return false
  return true
}

/**
 * Telemetria first-party: page views, tempo na página e origem da visita.
 */
export function ProductTelemetry() {
  const pathname = usePathname()
  const { user } = useAuth()
  // Admin não polui a telemetria: navegação da conta master é ignorada.
  const isAdmin = isAdminUserClient(user)
  const lastSentPath = useRef<string | null>(null)
  const activePath = useRef<string | null>(null)
  const pageEnteredAt = useRef<number>(Date.now())

  function sendEngagement(path: string, durationSeconds: number) {
    if (isAdmin) return
    if (durationSeconds < MIN_ENGAGEMENT_SEC) return
    sendTelemetryEvents([
      {
        event: PRODUCT_EVENTS.PAGE_ENGAGEMENT,
        category: EVENT_CATEGORIES.NAVIGATION,
        path,
        sessionId: getSessionId(),
        properties: {
          duration_seconds: durationSeconds,
          ...originProperties(),
          ...(user?.id ? { plano: user.plano } : { anonymous: true }),
        },
      },
    ])
  }

  function flushEngagement() {
    if (!activePath.current) return
    const seconds = Math.round((Date.now() - pageEnteredAt.current) / 1000)
    sendEngagement(activePath.current, seconds)
  }

  // Tempo na página ao sair / trocar aba
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flushEngagement()
    }
    const onPageHide = () => flushEngagement()

    document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      document.removeEventListener('visibilitychange', onHidden)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [user?.id, user?.plano, isAdmin])

  useEffect(() => {
    if (isAdmin) return
    if (!pathname || !shouldTrack(pathname)) return
    if (lastSentPath.current === pathname) return

    const normalized = normalizeTelemetryPath(pathname)

    // Fecha tempo na página anterior
    if (activePath.current && activePath.current !== normalized) {
      const seconds = Math.round((Date.now() - pageEnteredAt.current) / 1000)
      sendEngagement(activePath.current, seconds)
    }

    captureVisitOrigin(normalized)
    pageEnteredAt.current = Date.now()
    activePath.current = normalized
    lastSentPath.current = pathname

    const baseProps = {
      ...originProperties(),
      ...(user?.id ? { plano: user.plano } : { anonymous: true }),
    }

    // CALCULATOR_OPEN fica só no mount da calc (useCalcTelemetry → trackCalculadoraAberta).
    // Disparar aqui + no mount inflava "Abriu calculadora" ~2× no admin.
    sendTelemetryEvents([
      {
        event: PRODUCT_EVENTS.PAGE_VIEW,
        category: EVENT_CATEGORIES.NAVIGATION,
        path: normalized,
        sessionId: getSessionId(),
        properties: baseProps,
      },
    ])
  }, [pathname, user?.id, user?.plano, isAdmin])

  return null
}

/** Helper para eventos customizados no cliente. */
export function trackClientEvent(
  event: string,
  category: string,
  opts?: { path?: string; properties?: Record<string, unknown> },
) {
  sendTelemetryEvents([
    {
      event,
      category,
      path: opts?.path ? normalizeTelemetryPath(opts.path) : undefined,
      sessionId: getSessionId(),
      properties: { ...originProperties(), ...opts?.properties },
    },
  ])
}
